import { useEffect, useRef } from 'react';
import { useWindowSize } from '@/hooks/useWindowSize';
import { gsap } from 'gsap';

export function Parallax({
    className,
    children,
    speed = 1,
    id = 'parallax',
    position,
}: {
    className?: string;
    children: React.ReactNode;
    speed?: number;
    id?: string;
    position?: 'top' | 'bottom';
}) {
    const trigger = useRef<null | HTMLDivElement>(null);
    const target = useRef<null | HTMLDivElement>(null);
    const timeline = useRef<null | gsap.core.Timeline>(null);
    const { width: windowWidth } = useWindowSize();

    useEffect(() => {
        const y = windowWidth * speed * 0.1;
        const mm = gsap.matchMedia();

        timeline.current = gsap
            .timeline({
                scrollTrigger: {
                    id,
                    trigger: position === 'top' ? document.body : trigger.current,
                    scrub: true,
                    start: position === 'top' ? 'top top' : 'top bottom',
                    end: position === 'top' ? '+=100%' : 'bottom top',
                },
            })
            .fromTo(target.current, { y: position === 'top' ? 0 : -y }, { y: y, ease: 'none' });

        mm.add(
            {
                maxWidth: '(max-width: 1080px)',
                reduceMotion: '(prefers-reduced-motion: reduce)',
            },
            (context) => {
                const { reduceMotion, maxWidth } = context.conditions as {
                    reduceMotion: boolean;
                    maxWidth: boolean;
                };

                if (reduceMotion || maxWidth) {
                    timeline?.current?.from(target.current, { y: 0 });
                    timeline?.current?.kill();
                }
            }
        );

        return () => {
            timeline?.current?.kill();
        };
    }, [id, speed, position, windowWidth]);

    return (
        <div ref={trigger}>
            <div ref={target} className={className}>
                {children}
            </div>
        </div>
    );
}
